import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { toast } from 'react-toastify'
import classNames from 'classnames'

import classes from './PersonalForm.module.scss'
import Input from '../../common/form/Input'
import Select from '../../common/form/Select'
import moment from 'moment'
import { flatServerFormErrors } from '../../../utility/helperFunctions'
import { GENDER_SELECT_OPTIONS, FAMILY_ADMIN_MIN_AGE } from '../../../misc/Constants'
import { AccountContext } from '../AccountContext'
import { useContext } from 'react'

const CHANGE_EMAIL_URL = `${process.env.REACT_APP_OAUTH_PROVIDER_HOST}/profile/edit`

const ACCOUNT_TYPES = {
  ADULT_GOLFER: 'Adult Golfer (Admin)',
  JUNIOR_GOLFER: 'Junior Golfer',
  COACH: 'Coach',
}

const ACCOUNT_TYPE_DESCRIPTIONS = {
  ADULT_GOLFER: 'Family account manager. Can manage golfers, register for programs, access family settings, and track progress for all members.',
  JUNIOR_GOLFER: 'Can log rounds, track progress, and view their own activity. Family settings are managed by an Adult/Admin.',
  COACH: 'Program admin. Can manage students, run sessions, log 9-Hole Events, and track progress across the entire community.',
}

// eslint-disable-next-line max-lines-per-function, complexity
const PersonalForm = () => {
  const dispatch = useDispatch()
  const { user, submitDisabled, updateAction } = useContext(AccountContext) // Note: user could be golfer OR current_user
  const currentUser = useSelector(state => state.user.current_user)

  const [isUpdating, setIsUpdating] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  const [phoneData, setPhoneData] = useState({
    phone: user?.phone || '',
    phonePrefix: user?.phone_prefix || '',
    phonePrefixCountry: user?.phone_prefix_country || '',
  })

  // Note: DatePicker does not work as uncontrolled input; state only used to update the UI
  const [controlledBirthdate, setControlledBirthdate] = useState(user?.birthdate ? moment(user?.birthdate) : null)

  const isCurrentUser = user.id === currentUser.id
  const isCoach = currentUser.type === 'Coach'
  const isFamilyAdmin = currentUser.type === 'Student' && currentUser.family_admin
  const userIsCoach = user?.type === 'Coach'
  const userIsFamilyAdmin = user?.family_admin
  const familyAdminEditingJunior = isFamilyAdmin && !isCurrentUser && !userIsFamilyAdmin

  const accountTypeKey = userIsCoach ? 'COACH' : userIsFamilyAdmin ? 'ADULT_GOLFER' : 'JUNIOR_GOLFER'

  // Readonly when: coach editing others, non-family-admin editing others, or family admin editing another family admin
  const isReadonly = !isCurrentUser && (isCoach || !isFamilyAdmin || userIsFamilyAdmin)

  const validateForm = formData => {
    setErrors({})
    const formErrors = {}

    const firstName = formData.get('firstName')
    const lastName = formData.get('lastName')
    const birthdate = moment.utc(formData.get('birthdate'))

    if (!firstName) {
      formErrors['first_name'] = 'First name must be present'
    }

    if (!lastName) {
      formErrors['last_name'] = 'Last name must be present'
    }

    if (familyAdminEditingJunior && !formData.get('username')) {
      formErrors['username'] = 'Username must be present'
    }

    if (!birthdate.isValid()) {
      formErrors['birthdate'] = 'Date invalid'
    } else if (userIsFamilyAdmin && moment().diff(birthdate, 'years') < FAMILY_ADMIN_MIN_AGE) {
      formErrors['birthdate'] = `You must be at least ${FAMILY_ADMIN_MIN_AGE} years old`
    }

    if (Object.keys(formErrors)?.length) {
      setErrors(formErrors)
      return false
    }

    return true
  }

  const handleSubmitForm = async e => {
    e.preventDefault()
    const formData = new FormData(e.target)

    if (validateForm(formData)) {
      const partialUser = {
        id: user.id,
        first_name: formData.get('firstName'),
        last_name: formData.get('lastName'),
        ...(familyAdminEditingJunior && { username: formData.get('username') }),
        phone: phoneData.phone,
        phone_prefix: phoneData.phonePrefix,
        phone_prefix_country: phoneData.phonePrefixCountry,
        birthdate: moment.utc(formData.get('birthdate')).toDate(),
        gender: formData.get('gender'),
      }

      try {
        setIsUpdating(true)
        const newState = await dispatch(updateAction(partialUser))

        if (newState.type === 'UI_ERROR') {
          if (newState.status === 503) {
            toast.error('Updating your account is currently unavailable.')
          } else if (newState.error?.general) {
            toast.error(newState.error.general.join(', '))
          } else {
            setErrors(flatServerFormErrors(newState?.error))
          }
        } else {
          toast.success('User has been successfully updated!')
        }
      } catch {
        toast.error('Couldn\'t update user. Please check again later')
      } finally {
        setIsUpdating(false)
        setHasChanges(false)
      }
    }
  }

  const handleDetectChanges = (e) => {
    const formData = new FormData(e.currentTarget)
    const firstName = formData.get('firstName')
    const lastName = formData.get('lastName')
    const username = formData.get('username')
    const birthdate = moment.utc(formData.get('birthdate'))
    const gender = Number(formData.get('gender'))

    const hasChanges_ =
      user?.first_name !== firstName ||
      user?.last_name !== lastName ||
      (familyAdminEditingJunior && user?.username !== username) ||
      user?.phone !== phoneData.phone ||
      user?.phone_prefix !== phoneData.phonePrefix ||
      user?.phone_prefix_country !== phoneData.phonePrefixCountry ||
      user?.birthdate !== birthdate.toISOString() ||
      (user?.gender || '') !== (gender || '')

    if (hasChanges_ !== hasChanges) {
      setHasChanges(hasChanges_)
    }
  }

  const handleBirthdateSelect = (e) => {
    setControlledBirthdate(e)
    handleDetectChanges(e)
  }

  const handlePhoneChange = (phoneDataObj) => {
    setPhoneData({
      phone: phoneDataObj.phone || '',
      phonePrefix: phoneDataObj.phonePrefix || '',
      phonePrefixCountry: phoneDataObj.phonePrefixCountry || '',
    })
    setHasChanges(true)
  }

  return (
    <form onSubmit={handleSubmitForm} onInput={handleDetectChanges} className={classNames(classes.container, 'animated fadeIn')}>
      <div className='row'>
        <h4 className={classes.title}>Personal Details</h4>
      </div>

      <div className='row d-flex'>
        <div className='col-md-12 col-xl-6 p-0 pr-xl-4 mb-3'>
          <label htmlFor='firstName'>First Name</label>
          <Input
            type='text'
            id='firstName'
            name='firstName'
            defaultValue={user?.first_name}
            placeholder='First Name'
            classNames={{ inputContainer: classes.accountInputContainer }}
            disabled={isReadonly}
            error={errors?.first_name}
          />
        </div>
        <div className='col-md-12 col-xl-6 p-0 pl-xl-4 mb-3'>
          <label htmlFor='lastName'>Last Name</label>
          <Input
            type='text'
            id='lastName'
            name='lastName'
            defaultValue={user?.last_name}
            placeholder='Last Name'
            classNames={{ inputContainer: classes.accountInputContainer }}
            disabled={isReadonly}
            error={errors?.last_name}
          />
        </div>
      </div>

      {familyAdminEditingJunior && (
        <div className='row mb-3'>
          <label htmlFor='username'>Username</label>
          <Input
            type='text'
            id='username'
            name='username'
            defaultValue={user?.username}
            placeholder='Username'
            classNames={{ inputContainer: classes.accountInputContainer }}
            error={errors?.username}
          />
        </div>
      )}

      <div className='row mb-3'>
        <label htmlFor='phone'>Phone Number</label>
        <Input
          type='telephone'
          id='phone'
          name='phone'
          defaultValue={user?.phone_prefix && user?.phone ? `${user.phone_prefix}${user.phone}` : user?.phone}
          placeholder='Phone Number'
          classNames={{ inputContainer: classes.accountInputContainerPhone }}
          disabled={isReadonly}
          error={errors?.phone}
          onChange={handlePhoneChange}
        />
      </div>

      <div className='row'>
        <div className='col-md-12 col-xl-6 p-0 pr-xl-4 mb-3'>
          <label htmlFor='birthdate'>Birthdate</label>
          <Input
            type='date'
            id='birthdate'
            name='birthdate'
            placeholder='Birthdate'
            selected={controlledBirthdate}
            onChange={handleBirthdateSelect}
            classNames={{
              inputContainer: classes.accountInputContainer,
              symbol: classes.inputIcon,
            }}
            symbol={<i className='fa fa-calendar'/>}
            error={errors?.birthdate}
            disabled={isReadonly}
          />
        </div>
        <div className='col-md-12 col-xl-6 p-0 pl-xl-4 mb-3'>
          <label htmlFor='gender'>Gender</label>
          <Select
            id='gender'
            name='gender'
            placeholder='Select a gender'
            defaultValue={user?.gender !== undefined ? GENDER_SELECT_OPTIONS.find(gender => gender.value === user?.gender) : undefined}
            options={GENDER_SELECT_OPTIONS}
            onChange={handleDetectChanges}
            classNames={{ singleValue: () => classes.genderInputWrapper }}
            isDisabled={isReadonly}
            error={errors?.gender}
            isSearchable={false}
          />
        </div>
      </div>

      {isCurrentUser && (
        <div className='row mb-4'>
          <div className='col p-0'>
            <h5 className='mb-2'>{userIsFamilyAdmin ? 'Email' : 'Username'}</h5>
            <p className={classNames(classes.accountType, 'mb-1')}>
              {userIsFamilyAdmin ? user?.email : user?.username}
            </p>
            <p className={classes.infoText}>
              To change {userIsFamilyAdmin ? 'email' : 'username'}&nbsp;
              <a href={CHANGE_EMAIL_URL} target='_blank' rel='noopener noreferrer'>click here</a>.
            </p>
          </div>
        </div>
      )}

      <div className='row mb-4'>
        <div className='col p-0'>
          <h5 className='mb-2'>Account Type</h5>
          <p className={classNames(classes.accountType, 'mb-1')}>{ACCOUNT_TYPES[accountTypeKey]}</p>
          <p className={classes.infoText}>{ACCOUNT_TYPE_DESCRIPTIONS[accountTypeKey]}</p>
          {isCurrentUser && !userIsCoach && (
            <p className={classNames(classes.infoText, 'mt-2')}>
              To change account type, {userIsFamilyAdmin ? 'set a username instead of your email' : 'set an email instead of your username'}&nbsp;
              <a href={CHANGE_EMAIL_URL} target='_blank' rel='noopener noreferrer'>here</a>.
            </p>
          )}
        </div>
      </div>

      <div className='row mt-5'>
        <button
          type='submit'
          className='btn btn-primary'
          disabled={isUpdating || isReadonly || submitDisabled || !hasChanges}
        >
          Sav{isUpdating ? 'ing' : 'e'} Changes
        </button>
      </div>
    </form>
  )
}

export default PersonalForm
